import React, { useCallback, useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import { AccountCircleRounded } from '@mui/icons-material';

import LogoutIcon from '@mui/icons-material/Logout';
import { Link } from 'react-router-dom';
import WizTopBarEmailIntegrationSubmenu from './UserMenuSubmenus/WizTopBarEmailIntegrationSubmenu';
import WizTopBarESignSubmenu from './UserMenuSubmenus/WizTopBarESignSubmenu';
import WizTopBarOnboardingSubmenu from './UserMenuSubmenus/WizTopBarOnboardingSubmenu';
import WizTopBarTemplatesSubmenu from './UserMenuSubmenus/WizTopBarTemplatesSubmenu';
import { ExtendedUser } from 'common/models/Configuration/User';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { companyHasSinglePermission } from '../../util/PermissionsUtils';
import { Permission } from '../../common/models/Permissions';
import WizTopBarReferenceCheckSubmenu from './UserMenuSubmenus/WizTopBarReferenceCheckSubmenu';
import SendAdhocDocumentForESign from 'components/Dialogs/eSign/SendAdhocDocumentForESign';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

interface Props {
  currentUser: ExtendedUser | null;
  currentUserRefreshHandler: () => void;
  isSideBarOpen?: boolean;
}

const routerLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'none' };

export default function NavBarUserMenu({
  currentUser,
  currentUserRefreshHandler,
  isSideBarOpen = false,
}: Props) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [isRootHovered, setIsRootHovered] = useState(false);
  const [isRootPaperHovered, setIsRootPaperHovered] = useState(false);
  const [showSendAdhocDialog, setShowSendAdhocDialog] = useState(false);

  const handleOpenMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setIsRootHovered(true);
    setAnchor(e.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchor(null);
    setHoveredIndex(null);
  }, []);

  useEffect(() => {
    if (Boolean(anchor) && !isRootHovered && !isRootPaperHovered) setAnchor(null);
  }, [anchor, isRootHovered, isRootPaperHovered]);

  useEffect(() => {
    if (currentUser === null) currentUserRefreshHandler();
  }, [currentUser, currentUserRefreshHandler]);

  const handleHoverOption = useCallback((i: number | null) => {
    setHoveredIndex(i);
  }, []);

  return (
    <>
      <SendAdhocDocumentForESign
        open={showSendAdhocDialog}
        closeHandler={() => setShowSendAdhocDialog(false)}
      />
      <ListItem
        disablePadding
        sx={{ display: 'block', marginTop: '6px' }}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={() => setIsRootHovered(false)}
      >
        <Link to="/account" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Tooltip
            title="Account"
            placement="right"
            disableHoverListener={isSideBarOpen}
            slotProps={{
              popper: { modifiers: [{ name: 'offset', options: { offset: [-330, 0] } }] },
            }}
          >
            <ListItemButton
              sx={{ paddingY: 0, px: 2.5, justifyContent: isSideBarOpen ? 'initial' : 'center' }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, justifyContent: 'center', mr: isSideBarOpen ? 3 : 'auto' }}
              >
                <AccountCircleRounded
                  sx={{ color: (t) => t.palette.getContrastText(t.palette.primary.main) }}
                />
              </ListItemIcon>
              <ListItemText primary="Account" sx={{ opacity: isSideBarOpen ? 1 : 0 }} />
            </ListItemButton>
          </Tooltip>
        </Link>
      </ListItem>
      <Popper
        anchorEl={anchor}
        open={Boolean(anchor)}
        placement="right-end"
        sx={{ zIndex: (t) => t.zIndex.appBar + 2 }}
        onMouseEnter={() => setIsRootPaperHovered(true)}
        onMouseLeave={() => setIsRootPaperHovered(false)}
      >
        <ClickAwayListener onClickAway={handleCloseMenu}>
          <Paper elevation={8} onMouseLeave={handleCloseMenu}>
            <Link to="/account" style={{ color: 'inherit', textDecoration: 'none' }}>
              <MenuItem onClick={handleCloseMenu} onMouseEnter={() => handleHoverOption(3)}>
                Account
              </MenuItem>
            </Link>
            <Divider sx={{ my: 1 }} />
            <WizTopBarEmailIntegrationSubmenu
              index={4}
              hoveredIndex={hoveredIndex}
              onMouseEnter={handleHoverOption}
              closeUserMenuHandler={handleCloseMenu}
            />
            <WizTopBarESignSubmenu
              index={5}
              hoveredIndex={hoveredIndex}
              onMouseEnter={handleHoverOption}
              closeUserMenuHandler={handleCloseMenu}
              openSendAdhocDocumentDialogHandler={() => setShowSendAdhocDialog(true)}
            />
            <WizTopBarReferenceCheckSubmenu
              index={6}
              hoveredIndex={hoveredIndex}
              onMouseEnter={handleHoverOption}
              closeUserMenuHandler={handleCloseMenu}
            />

            <Link to="/account/integrations" style={routerLinkStyle}>
              <MenuItem onClick={handleCloseMenu} onMouseEnter={() => handleHoverOption(6)}>
                Integrations
              </MenuItem>
            </Link>
            {companyHasSinglePermission(Permission.Onboarding) && (
              <WizTopBarOnboardingSubmenu
                index={7}
                hoveredIndex={hoveredIndex}
                onMouseEnter={handleHoverOption}
                closeUserMenuHandler={handleCloseMenu}
              />
            )}
            <Link to="/account/saved-searches" style={routerLinkStyle}>
              <MenuItem onClick={handleCloseMenu} onMouseEnter={() => handleHoverOption(9)}>
                Saved Searches
              </MenuItem>
            </Link>
            <WizTopBarTemplatesSubmenu
              index={14}
              hoveredIndex={hoveredIndex}
              onMouseEnter={handleHoverOption}
              closeUserMenuHandler={handleCloseMenu}
            />
            <Divider />
            <Link to="/logout" style={routerLinkStyle}>
              <MenuItem onClick={handleCloseMenu} onMouseEnter={() => handleHoverOption(10)}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Link>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
